import { FC, useCallback, useRef, useState, useEffect } from 'react';
import { UserCircleIcon, ArrowRightOnRectangleIcon, UserIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@data/contexts';
import { useNavigation } from '@utilities/hooks';
import { NavigationPathsEnum } from '@utilities/enums';

export const Header: FC = () => {
    const { admin, logout } = useAuth();
    const { navigateTo } = useNavigation();
    const [popoverOpen, setPopoverOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    const displayName = admin?.firstName || admin?.lastName
        ? `${admin.firstName} ${admin.lastName}`.trim()
        : admin?.email || '';

    const handleLogout = useCallback(() => {
        setPopoverOpen(false);
        logout();
        navigateTo(NavigationPathsEnum.LOGIN);
    }, [logout, navigateTo]);

    const handleGoToProfile = useCallback(() => {
        setPopoverOpen(false);
        navigateTo(NavigationPathsEnum.PROFILE);
    }, [navigateTo]);

    // Fermer le popover en cliquant en dehors
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setPopoverOpen(false);
            }
        };
        if (popoverOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [popoverOpen]);

    return (
        <header className="h-16 bg-base-100 border-b border-base-300 flex items-center justify-end px-6">
            <div className="relative" ref={popoverRef}>
                <button
                    onClick={() => setPopoverOpen(!popoverOpen)}
                    className="flex items-center gap-3 hover:bg-base-200 rounded-lg px-3 py-2 transition-colors"
                >
                    <span className="text-sm font-medium">{displayName}</span>
                    <UserCircleIcon className="w-8 h-8 text-base-content/60" />
                </button>

                {popoverOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-base-100 rounded-lg shadow-lg border border-base-300 z-50 animate-fadeIn">
                        <div className="p-3 border-b border-base-300">
                            <p className="text-sm font-medium truncate">{displayName}</p>
                            <p className="text-xs text-base-content/60 truncate">{admin?.email}</p>
                        </div>
                        <div className="p-1">
                            <button
                                onClick={handleGoToProfile}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-base-200 transition-colors text-sm"
                            >
                                <UserIcon className="w-4 h-4" />
                                <span>Mon profil</span>
                            </button>
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-base-200 transition-colors text-sm text-error"
                            >
                                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                                <span>Se déconnecter</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </header>
    );
};
